import { useContext, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import {
  addWeeks,
  format,
  startOfWeek,
  subWeeks,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  startOfDay,
  endOfDay,
  subDays,
  addDays,
} from "date-fns";
import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";
import {
  Calendar,
  Button,
  FilterHeader,
  IconButton,
  Icons,
  Text,
  useStoredView,
  SelectInput,
  SlotSelection,
} from "@pushpress/shared-components";
import { env } from "../../env";
import { AppContext } from "../../context/app";
import Dashboard from "../../layouts/Dashboard";
import useIsMobile from "../../hooks/useIsMobile";
import {
  defaultToastOptions,
  extractError,
  joinStrings,
} from "../../utils/misc";
import { useAppointments } from "../Appointments/AppointmentsProvider/AppointmentsProvider";
import {
  filterAppointmentsItems,
  filterCalendarItems,
  getCalendarFilterTagsMap,
  CalendarItemSessionTypes,
  convertToTimezone,
} from "./Calendar.utils";
import {
  AppointmentScheduleToCalendarFragment,
  CalendarItemFragment,
  useGetAppointmentSchedulesQuery,
  useGetCalendarItemsQuery,
} from "./Calendar-generated.hooks";
import CalendarFilter from "./CalendarFilter/CalendarFilter";
import { ICalendarFilter } from "./Calendar.types";
import CalendarHeader from "./CalendarHeader";
import { useFeatureFlag } from "../../context/features";
import { APPOINTMENTS_FEATURE_FLAG } from "../../context/features/features.utils";
import CalendarActionsRouter from "./CalendarActionsRouter";
import { useHistory, useRouteMatch } from "react-router-dom";
import { isCypress } from "../../utils/cypress";
import { getDateTitle } from "../../utils/dateUtils";
import styles from "./Calendar.module.scss";
import QuickEditModal from "./CalendarForm/QuickEditModal";

export const CalendarContent = () => {
  // hooks

  const history = useHistory();
  const { isFeatureEnabled } = useFeatureFlag();
  const routeMatch = useRouteMatch(`/schedule`);
  const { client } = useContext(AppContext);
  const appointmentsEnabled = isFeatureEnabled(APPOINTMENTS_FEATURE_FLAG);
  const { showScheduleModal, showBookAppointmentsDrawer } = useAppointments();
  const [quickEditCalendarItemUuid, setQuickEditCalendarItemUuid] =
    useState<string>();
  const isMobile = useIsMobile();
  const path = isCypress ? `/schedule` : routeMatch?.path;
  const clientUuid = client.uuid || client.client_uuid;
  const timezone = client.timezone;

  // states

  const [date, setDate] = useState(new Date());

  const [view, setView] = useState<"day" | "week" | "month">("week");

  const {
    filter,
    filterTagOptions,
    viewLoading,
    viewSaved,
    handleFilterChange,
    handleFilterTagDelete,
    handleViewSave,
  } = useStoredView<ICalendarFilter>({
    viewSaveId: "calendar-view",
    defaultFilter: {
      staff: [],
      locations: [],
    },
    getFilterTagsMap: getCalendarFilterTagsMap,
  });

  // api

  let startDate = startOfDay(date);
  let endDate = endOfDay(date);
  if (view === "week") {
    startDate = startOfWeek(date);
    endDate = endOfWeek(date);
  } else if (view === "month") {
    startDate = startOfMonth(date);
    endDate = endOfMonth(date);
  }

  const {
    data: calendarData,
    refetch,
    updateQuery: updateGetCalendarItems,
  } = useGetCalendarItemsQuery({
    variables: {
      getCalendarItemsInput: {
        startDate: format(startDate, "yyyy-MM-dd"),
        endDate: format(endDate, "yyyy-MM-dd"),
        calendarSessionTypeId: CalendarItemSessionTypes.All,
        clientUuid,
        isPublicOnly: false,
      },
    },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    onError(error) {
      toast.error(extractError(error), defaultToastOptions);
    },
  });

  const {
    data: { getAppointmentSchedules: appointmentsData } = {},
    refetch: appointmentsRefetch,
  } = useGetAppointmentSchedulesQuery({
    variables: {
      from: zonedTimeToUtc(startDate, timezone).toISOString(),
      to: zonedTimeToUtc(endDate, timezone).toISOString(),
      timeZone: client.timezone,
    },
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    skip: !appointmentsEnabled,
    onError(error) {
      toast.error(extractError(error), defaultToastOptions);
    },
  });

  //  callbacks

  const handlePrevious = () => {
    if (view === "day") {
      setDate((prev) => subDays(prev, 1));
    } else if (view === "week") {
      setDate((prev) => subWeeks(prev, 1));
    } else if (view === "month") {
      setDate((prev) => subMonths(prev, 1));
    }
  };

  const handleNext = () => {
    if (view === "day") {
      setDate((prev) => addDays(prev, 1));
    } else if (view === "week") {
      setDate((prev) => addWeeks(prev, 1));
    } else if (view === "month") {
      setDate((prev) => addMonths(prev, 1));
    }
  };

  const handleEventClick = (
    event: CalendarItemFragment | AppointmentScheduleToCalendarFragment,
  ) => {
    if (event.__typename === "Class") {
      setQuickEditCalendarItemUuid(event.uuid);
    } else if (event.__typename === "AppointmentSchedule") {
      showScheduleModal(event.uuid, {
        onSuccess: () => appointmentsRefetch(),
      });
    }
  };

  const handleSlotsSelect = (selection: SlotSelection) => {
    const { start, end } = selection;
    const initialDate = formatInTimeZone(start, "UTC", "yyyy-MM-dd");
    const initialHour = formatInTimeZone(start, "UTC", "hh:mm aa");
    const endHour = formatInTimeZone(end, "UTC", "hh:mm aa");
    history.push(
      `${path}/create/class?initialDate=${initialDate}&initialHour=${initialHour}&endHour=${endHour}`,
    );
  };

  const handleCreateAppointmentClick = () => {
    showBookAppointmentsDrawer({}, { onSuccess: () => appointmentsRefetch() });
  };

  const handleEditCalendarItemCoach = (
    calendarItemUuid: string,
    coach: { userUuid: string; firstName: string; lastName: string },
  ) => {
    updateGetCalendarItems((previousResult) => {
      const getCalendarItems = previousResult.getCalendarItems.map((item) => {
        if (item.uuid !== calendarItemUuid) {
          return item;
        }
        return {
          ...item,
          mainCoach: coach,
        };
      });
      return {
        ...previousResult,
        getCalendarItems,
      };
    });
  };

  const eventAccessor = (
    event: CalendarItemFragment | AppointmentScheduleToCalendarFragment,
  ) => {
    if (event.__typename === "Class") {
      const startFormatted = formatInTimeZone(
        new Date(event.startDatetime),
        "UTC",
        "yyyy-MM-dd",
      );
      const endFormatted = formatInTimeZone(
        new Date(event.endDatetime),
        "UTC",
        "yyyy-MM-dd",
      );
      const sameDay = startFormatted === endFormatted;
      return {
        id: event.uuid,
        title: event.title,
        start: new Date(event.startDatetime),
        end: new Date(event.endDatetime),
        color: event.calendarItemType?.color,
        coach: event.mainCoach
          ? `${event.mainCoach.firstName} ${event.mainCoach.lastName}`
          : undefined,
        allDay: event.isAllDay || !sameDay,
      };
    } else {
      return {
        id: event.uuid,
        title: joinStrings(event.user?.firstName, event.user?.lastName),
        start: convertToTimezone(new Date(event.startTime), timezone),
        end: convertToTimezone(new Date(event.endTime), timezone),
        color: event.appointmentType.color,
        coach: event.staff
          ? `${event.staff.firstName} ${event.staff.lastName}`
          : undefined,
        allDay: false,
      };
    }
  };

  // memos

  const data = useMemo(() => {
    const filteredCalendarItems = filterCalendarItems(
      calendarData?.getCalendarItems || [],
      filter,
    );
    const filteredAppointmentsItems = filterAppointmentsItems(
      appointmentsData || [],
      filter,
    );
    return [...filteredCalendarItems, ...filteredAppointmentsItems];
  }, [calendarData, appointmentsData, filter]);

  const title = useMemo<string>(() => {
    return getDateTitle({ date, view, isMobile });
  }, [date, view, isMobile]);

  return (
    <>
      <CalendarActionsRouter refetch={refetch} baseUrl={path} />
      <QuickEditModal
        visible={!!quickEditCalendarItemUuid}
        uuid={quickEditCalendarItemUuid}
        onClose={() => setQuickEditCalendarItemUuid(undefined)}
        onCancel={() => {
          refetch?.();
          setQuickEditCalendarItemUuid(undefined);
        }}
        onEditClick={(calendarItem) => {
          setQuickEditCalendarItemUuid(undefined);
          if (calendarItem.type === "Class") {
            history.push(`${path}/edit/class/${calendarItem.uuid}`);
          } else if (calendarItem.type === "Event") {
            history.push(`${path}/edit/event/${calendarItem.uuid}`);
          }
        }}
        onDuplicateClick={(calendarItem) => {
          history.push(`${path}/duplicate/class/${calendarItem.uuid}`);
        }}
        onMainCoachChange={handleEditCalendarItemCoach}
      />
      <div className={styles.pageHeader}>
        <CalendarHeader
          onCreateClassClick={() => history.push(`${path}/create/class`)}
          onCreateEventClick={() => history.push(`${path}/create/event`)}
          onCreateAppointmentClick={handleCreateAppointmentClick}
        />
      </div>
      <div className={styles.calendarHeader}>
        <FilterHeader
          filter={filter}
          filterTagOptions={filterTagOptions}
          viewLoading={viewLoading}
          viewSaved={viewSaved}
          onFilterChange={handleFilterChange}
          onViewSave={handleViewSave}
          onFilterTagDelete={handleFilterTagDelete}
          renderFilter={(
            filter?: ICalendarFilter,
            handleFilterChange?: (filter?: ICalendarFilter) => void,
          ) => {
            return (
              <CalendarFilter
                filter={filter}
                onFilterChange={handleFilterChange}
              />
            );
          }}
        >
          <div className={styles.actions}>
            <div className={styles.today}>
              <Button
                buttonType="secondary"
                text={"Today"}
                onClick={() => setDate(new Date())}
              />
            </div>
            <div className={styles.nav}>
              <IconButton
                icon={Icons.CaretLeft}
                onClick={handlePrevious}
                aria-label="Previous"
              />
              <IconButton
                icon={Icons.CaretRight}
                onClick={handleNext}
                aria-label="Next"
              />
            </div>
            <div className={styles.title}>
              <Text variant={isMobile ? "heading-5" : "heading-4"}>
                {title}
              </Text>
            </div>
          </div>
        </FilterHeader>
        <div className={styles.viewSelector}>
          <SelectInput
            value={view}
            onValueChange={setView}
            inputProps={{ "aria-label": "Select the view" }}
            options={[
              { value: "day", label: "Day" },
              { value: "week", label: "Week" },
              { value: "month", label: "Month" },
            ]}
          />
        </div>
      </div>
      <div className={styles.calendarContent}>
        <Calendar
          view={view}
          date={date}
          timezone="UTC"
          stickyHeaders
          stickyTimeColumn
          minColumnWidth={160}
          events={data}
          nowIndicator
          nowTimezone={timezone}
          slotsSelectable
          firstDay={0}
          schedulerLicenseKey={env.FULL_CALENDAR_LICENSE_KEY}
          eventAccessor={eventAccessor}
          onEventClick={handleEventClick}
          onSlotsSelect={handleSlotsSelect}
        />
      </div>
    </>
  );
};

function CalendarPage() {
  return (
    <>
      <Helmet>
        <title>Calendar</title>
      </Helmet>
      <Dashboard scrollableContent disableContentPadding>
        <CalendarContent />
      </Dashboard>
    </>
  );
}

export default CalendarPage;
